import React, { useEffect, useMemo, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSave,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = "https://nissi-india.com/api/purchaseOrderListing";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toISO = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toDMY = (iso) => {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const PurchaseOrderComponent = () => {
  const navigate = useNavigate();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState("Purchase Order");

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  // ✅ ASP defaults: from = today-7, to = today
  const [fromDate, setFromDate] = useState(() =>
    toISO(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
  );
  const [toDate, setToDate] = useState(() => toISO(new Date()));

  const [ddl, setDdl] = useState([]);
  const [selectedPo, setSelectedPo] = useState("%");

  const [loadingDDL, setLoadingDDL] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const contractId = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";

    const cid = Number(fromUrl || fromSession || 0);
    return Number.isFinite(cid) ? cid : 0;
  }, []);

  const withContractId = (path) => {
    if (!contractId) return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ") },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  const loadDropdown = async () => {
    setLoadingDDL(true);
    setError("");

    try {
      if (!branchId || branchId <= 0) {
        setDdl([]);
        setSelectedPo("%");
        setError("Branch ID missing in localStorage");
        return;
      }

      if (!fromDate || !toDate) {
        setDdl([]);
        setSelectedPo("%");
        return;
      }

      const res = await axios.get(`${API_BASE}/dropdown`, {
        params: {
          branchId,
          fromDate,
          toDate,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setDdl(res.data?.purchaseOrders || []);
        setSelectedPo("%");
      } else {
        setDdl([]);
        setSelectedPo("%");
        setError(res.data?.message || "Failed to load dropdown");
      }
    } catch (e) {
      console.error(e);
      setDdl([]);
      setSelectedPo("%");
      setError("Server error");
    } finally {
      setLoadingDDL(false);
    }
  };

  useEffect(() => {
    const moduleSelect =
      sessionStorage.getItem("ModuleSelect") ||
      sessionStorage.getItem("moduleSelect") ||
      "ERP";

    if (String(moduleSelect).toUpperCase() !== "ERP") {
      window.location.href = "/ModuleHomePage";
      return;
    }

    loadDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, branchId]);

  const detectOption = (row) => {
    const candidates = [
      row?.purchaseOrderNo,
      row?.pono,
      row?.fld_pono,
      row?.fld_PoNo,
      row?.PoNo,
      row?.PONo,
      row?.PO_NO,
      row?.PurchaseOrderNo,
    ].filter((x) => x !== undefined && x !== null && String(x).trim() !== "");

    if (candidates.length) return String(candidates[0]);

    for (const k of Object.keys(row || {})) {
      const v = row[k];
      if (typeof v === "string" || typeof v === "number") return String(v);
    }
    return "";
  };

  const handleSave = async () => {
    if (saving) return;

    if (!contractId) {
      alert("contractId missing");
      return;
    }

    if (!loginUserId) {
      alert("loginUserId missing in localStorage");
      return;
    }

    if (!selectedPo || selectedPo === "%") {
      alert("Please select Purchase OrderNo");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const res = await axios.post(
        `${API_BASE}/save`,
        {
          contractId,
          loginUserId,
          purchaseOrderNo: selectedPo,
        },
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        alert(res.data?.message || "Saved Successfully");
        navigate(`/OrderManagement/PurchaseOrderListing?contractId=${contractId}`);
      } else {
        alert(res.data?.message || "Save failed");
      }
    } catch (e) {
      console.error(e);
      setError("Server error");
      alert("Server error");
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    switch (activeMenuItem) {
      case "Purchase Order":
        return (
          <div
            className={`rounded-2xl shadow-xl border ${
              isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div className="bg-blue-600 text-white px-6 py-4 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">PURCHASE ORDER</h2>
                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-green-500 hover:bg-green-400 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                  >
                    <FaSave />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div>
                  <label className={`block text-sm mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    (dd/MM/yyyy: {toDMY(fromDate)})
                  </div>
                </div>

                <div>
                  <label className={`block text-sm mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    (dd/MM/yyyy: {toDMY(toDate)})
                  </div>
                </div>

                <div>
                  <label className={`block text-sm mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Purchase OrderNo
                  </label>
                  <select
                    value={selectedPo}
                    onChange={(e) => setSelectedPo(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="%">Select</option>
                    {(ddl || []).map((row, idx) => {
                      const v = detectOption(row);
                      return (
                        <option key={idx} value={v}>
                          {v}
                        </option>
                      );
                    })}
                  </select>

                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {loadingDDL ? "Loading purchase orders..." : `Found: ${ddl?.length || 0}`}
                  </div>
                </div>
              </div>

              {error ? (
                <div className={`mt-6 text-center text-sm ${isDark ? "text-red-300" : "text-red-600"}`}>
                  {error}
                </div>
              ) : null}
            </div>
          </div>
        );

      default:
        return (
          <div
            className={`rounded-2xl shadow-xl border ${
              isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
              <h2 className="text-lg font-bold">{activeMenuItem.toUpperCase()} MANAGEMENT</h2>
            </div>
            <div className="p-6">
              <p className={`text-center py-8 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {activeMenuItem} content will be displayed here.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                onClick={() => setActiveMenuItem(item.name)}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  window.location.pathname === new URL(item.path, window.location.origin).pathname
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex-1">
          <div
            className={`transition-all duration-300 min-h-screen ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            <div className="p-6">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderComponent;